import { useCallback, useMemo } from 'react';
import { 
  Copy, Clipboard, Files, Trash2, Group as GroupIcon, Ungroup, 
  ArrowUp, ChevronUp, ChevronDown, ArrowDown 
} from 'lucide-react';
import { ContextMenuItem } from '../../components';
import { UseCanvasStateReturn } from './useCanvasState';
import { useLayerOrdering } from './useLayerOrdering';

export function useContextMenu(
  state: UseCanvasStateReturn,
  handleDelete: (ids: string[]) => void
) {
  const {
    isEditMode,
    selectedIds,
    elements,
    contextMenu,
    setContextMenu,
    clipboardManagerRef,
    groupingManagerRef,
  } = state;

  const layerOrdering = useLayerOrdering(state);

  // Context menu handling
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    // Show context menu even if no elements are selected (right-click on free space)
    setContextMenu({ x: e.clientX, y: e.clientY, selectedIds: [...selectedIds] });
  }, [isEditMode, selectedIds, setContextMenu]);

  // Context menu items
  const contextMenuItems: ContextMenuItem[] = useMemo(() => {
    const menuSelection = contextMenu?.selectedIds || selectedIds;
    // Use elements for data lookup (positions come from Konva)
    const selectedGroup = menuSelection.length === 1
      ? elements.find((el) => el.id === menuSelection[0])
      : null;
    const isSelectedGroup = selectedGroup?.type === 'group';
    
    return [
      ...(menuSelection.length > 1 ? [
        { id: 'selection-header', label: `${menuSelection.length} items selected`, disabled: true },
        { divider: true, id: 'divider-header', label: '' },
      ] : []),
      { 
        id: 'copy', 
        label: 'Copy', 
        icon: Copy, 
        shortcut: 'Ctrl+C', 
        disabled: menuSelection.length === 0, 
        onClick: () => {
          // Track that copy was done via context menu
          clipboardManagerRef.current?.copy(elements, menuSelection, true);
        }
      },
      { 
        id: 'paste', 
        label: 'Paste', 
        icon: Clipboard, 
        shortcut: 'Ctrl+V', 
        disabled: !clipboardManagerRef.current?.hasClipboard(), 
        onClick: () => {
          // Get paste position from context menu location (convert screen to layer coordinates)
          const canvas = state.canvasRef.current;
          if (canvas && contextMenu) {
            const stage = canvas.getStage();
            const container = state.containerRef.current;
            if (stage && container) {
              // Convert context menu client coordinates to canvas-relative coordinates
              const rect = container.getBoundingClientRect();
              const canvasX = contextMenu.x - rect.left;
              const canvasY = contextMenu.y - rect.top;
              // Convert to layer coordinates
              const layerPos = canvas.screenToLayer({ x: canvasX, y: canvasY });
              clipboardManagerRef.current?.paste(layerPos);
            } else {
              clipboardManagerRef.current?.paste();
            }
          } else {
            clipboardManagerRef.current?.paste();
          }
          // Reset context menu flag after paste
          clipboardManagerRef.current?.resetContextMenuFlag();
        }
      },
      { 
        id: 'duplicate', 
        label: 'Duplicate', 
        icon: Files, 
        shortcut: 'Ctrl+D', 
        disabled: menuSelection.length === 0, 
        onClick: () => clipboardManagerRef.current?.duplicate(elements, menuSelection) 
      },
      { divider: true, id: 'divider1', label: '' },
      { 
        id: 'delete', 
        label: menuSelection.length > 1 ? `Delete ${menuSelection.length} items` : 'Delete', 
        icon: Trash2, 
        shortcut: 'Del', 
        disabled: menuSelection.length === 0, 
        onClick: () => handleDelete(menuSelection) 
      },
      { divider: true, id: 'divider2', label: '' },
      { 
        id: 'group', 
        label: 'Group', 
        icon: GroupIcon, 
        shortcut: 'Ctrl+G', 
        disabled: menuSelection.length < 2, 
        onClick: () => groupingManagerRef.current?.group(elements, menuSelection) 
      },
      { 
        id: 'ungroup', 
        label: 'Ungroup', 
        icon: Ungroup, 
        shortcut: 'Ctrl+Shift+G', 
        disabled: menuSelection.length !== 1 || !isSelectedGroup, 
        onClick: () => groupingManagerRef.current?.ungroup(elements, menuSelection[0]) 
      },
      { divider: true, id: 'divider3', label: '' },
      { id: 'bring-front', label: 'Bring to Front', icon: ArrowUp, shortcut: ']', disabled: menuSelection.length === 0, onClick: layerOrdering.handleBringToFront },
      { id: 'bring-forward', label: 'Bring Forward', icon: ChevronUp, shortcut: ']', disabled: menuSelection.length === 0, onClick: layerOrdering.handleBringForward },
      { id: 'send-backward', label: 'Send Backward', icon: ChevronDown, shortcut: '[', disabled: menuSelection.length === 0, onClick: layerOrdering.handleSendBackward },
      { id: 'send-back', label: 'Send to Back', icon: ArrowDown, shortcut: '[', disabled: menuSelection.length === 0, onClick: layerOrdering.handleSendToBack },
    ];
  }, [contextMenu, selectedIds, elements, handleDelete, layerOrdering]);

  return {
    handleContextMenu,
    contextMenuItems,
    contextMenu,
    setContextMenu,
  };
}
